import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { filter, Subscription } from 'rxjs';

import { IntegrationConfigField, IntegrationSubSectionRecord } from '../../interface/integrationmodal.interface';

@Component({
    selector: 'app-code-editor',
    templateUrl: './code-editor.component.html',
    styleUrls: ['./code-editor.component.scss'],
    standalone: false,
})
export class CodeEditorComponent implements OnInit, OnDestroy {
    @Input() placeholder: string;
    @Input() model: any;
    @Input() id: string;
    @Input() required: boolean;
    @Input() editable = true;
    @Input() errors: any[];
    @Input() language = 'raw';
    @Input() theme = 'vs-dark';
    @Input() tailwindClasses: string;
    @Input() isBodyEditor = false;
    @Input() record: IntegrationSubSectionRecord;

    @Output() modelChange = new EventEmitter<string>();

    private control = new UntypedFormControl('');
    private valueSubscription: Subscription;
    public lineNumbers: number[] = [];
    public languageOptions = [
        {
            value: 'raw',
            displayName: 'Raw',
        },
        {
            value: 'graphql',
            displayName: 'GraphQL',
        },
        {
            value: 'jsonrpc',
            displayName: 'JSON-RPC',
        },
        {
            value: 'formUrlEncodedParams',
            displayName: 'x-www-form-urlencoded',
        },
    ];
    public queryField: IntegrationConfigField;
    public variablesField: IntegrationConfigField;
    public bodyField: IntegrationConfigField;
    public bodyTypeField: IntegrationConfigField;
    public rpcMethodField: IntegrationConfigField;
    public rpcParamsField: IntegrationConfigField;
    public urlencodedKeyField: IntegrationConfigField;
    public urlencodedValueField: IntegrationConfigField;

    constructor() {}

    public checkBodyType = () => {
        const languageField = this.record?.fields?.find((field: any) => field.id === 'language');
        switch (this.language) {
            case 'graphql':
                languageField.value = undefined;
                this.bodyField.value = '';
                this.rpcMethodField.value = undefined;
                this.rpcParamsField.value = undefined;
                if (!this.queryField.value || this.queryField.value?.length === 0) {
                    this.queryField.value = 'query ';
                }
                if (!this.variablesField.value || this.variablesField.value?.length === 0) {
                    this.variablesField.value = '{\n\n}';
                }
                break;
            case 'raw':
                languageField.value = undefined;
                this.queryField.value = undefined;
                this.variablesField.value = undefined;
                this.rpcMethodField.value = undefined;
                this.rpcParamsField.value = undefined;
                this.bodyField.value = this.bodyField.value || '';
                break;
            case 'jsonrpc':
                this.bodyField.value = '';
                this.queryField.value = undefined;
                this.variablesField.value = undefined;
                this.rpcMethodField.value = this.rpcMethodField?.value || '';
                this.rpcParamsField.value = this.rpcParamsField?.value ? JSON.parse(this.rpcParamsField.value) : '{\n\n}';
                break;
            case 'formUrlEncodedParams':
                this.queryField.value = undefined;
                this.variablesField.value = undefined;
                this.rpcMethodField.value = undefined;
                this.rpcParamsField.value = undefined;
                this.bodyField.value = undefined;
                break;
        }
        if (languageField) {
            languageField.value = this.language;
        }
    };

    ngOnInit() {
        if (this.isBodyEditor) {
            this.queryField = this.record?.fields?.find((field: any) => field.type === 'QUERY_EDITOR');
            this.variablesField = this.record?.fields?.find((field: any) => field.type === 'VARIABLES_EDITOR');
            this.bodyTypeField = this.record?.fields?.find((field: any) => field.type === 'BODY_TYPE');
            this.bodyField = this.record?.fields?.find((field: any) => field.type === 'BODY_EDITOR');
            this.rpcMethodField = this.record?.fields?.find((field: any) => field.type === 'JSON_RPC_METHOD');
            this.rpcParamsField = this.record?.fields?.find((field: any) => field.type === 'JSON_RPC_PARAMS');
            this.variablesField.value = this.variablesField?.value === 'null' ? undefined : this.variablesField?.value;
            this.rpcParamsField.value = this.rpcParamsField?.value === 'null' ? undefined : this.rpcParamsField?.value;
            const bodyContent = this.bodyField?.value;
            const hasBody = !!bodyContent?.length;
            const hasGraphql = !!(this.queryField?.value?.length || this.variablesField?.value?.length);
            const hasJsonRpc = !!(this.rpcMethodField?.value?.length || this.rpcParamsField?.value?.length);
            const languageField = this.record?.fields?.find((field: any) => field.id === 'language');
            const hasformUrlEncodedField =
                this.record.childSubSections?.find((field: any) => field.id === 'formUrlEncodedParams')?.records?.length > 0;
            switch (true) {
                case hasBody || (!hasGraphql && !hasJsonRpc && !hasformUrlEncodedField):
                    this.language = 'raw';
                    break;
                case hasGraphql:
                    this.language = 'graphql';
                    break;
                case hasJsonRpc:
                    this.language = 'jsonrpc';
                    break;
                case hasformUrlEncodedField:
                    this.language = 'formUrlEncodedParams';
                    break;
                default:
                    this.language = 'raw';
                    break;
            }
            if (languageField) {
                languageField.value = this.language;
            }
            this.checkBodyType();
        }
        if (typeof this.model === 'object') {
            this.model = JSON.stringify(this.model);
        }
        this.updateLineNumbers(this.model);
        if (this.model?.length > 0) {
            this.control.setValue(this.model);
        }

        this.valueSubscription = this.control.valueChanges.pipe(filter(data => data !== undefined)).subscribe(value => {
            this.modelChange.emit(value);
            this.updateLineNumbers(value);
            if (this.errors && this.errors.length > 0) {
                this.errors.splice(0, this.errors.length);
            }
        });
    }

    updateLineNumbers(text: string) {
        const lines = text?.split('\n') || [];
        this.lineNumbers = Array.from({ length: lines?.length + 4 }, (_, i) => i + 1);
    }

    ngOnDestroy() {
        if (this.valueSubscription) {
            this.valueSubscription.unsubscribe();
        }
    }
}
